import { env, IS_LOCAL } from "@/env";
import { logger } from "@utilities/logger";

import { captureEvent } from "../../_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

export interface GetCaptchaTokenProps {
  behaviourType:
    | "creative_template"
    | "sound_effects"
    | "stt"
    | "tts"
    | "subtitles"
    | "contact_sales"
    | "voice_cloning"
    | "agent_request_call"
    | "agent_verify_phone"
    | "agent_template_preview"
    | "translate-media" // Named this to keep consistent with backend bq naming
    | "sign_up"
    | "sign_in";
  sitekey?: string;
}

export async function getCaptchaToken({ behaviourType, sitekey }: GetCaptchaTokenProps): Promise<string> {
  if (IS_LOCAL) return "20000000-aaaa-bbbb-cccc-000000000002"; //
  // wait 5s for hcaptcha to be defined
  await new Promise((resolve, reject) => {
    const CHECK_INTERVAL = 100;
    const TIMEOUT = 8000;
    let attempts = 0;
    const id = setInterval(() => {
      attempts++;
      if (typeof window !== "undefined" && window.hcaptcha) {
        clearInterval(id);
        resolve(true);
      } else if (attempts * CHECK_INTERVAL > TIMEOUT) {
        captureEvent(EventName.mw_hcaptcha_script_timeout, { behaviour_type: behaviourType });
        reject(new Error("hcaptcha script failed to load"));
      }
    }, CHECK_INTERVAL);
  });

  // Create the container for the captcha
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-999999px";
  document.body.appendChild(container);

  const hcaptcha = window.hcaptcha;

  return new Promise((resolve, reject) => {
    const widget = hcaptcha.render(container, {
      sitekey: sitekey ?? env.NEXT_PUBLIC_HCAPTCHA_SITEKEY_ANON_REQUEST,
      size: "invisible",
      callback: (token: string) => {
        resolve(token);
        cleanup();
      },
      "open-callback": () => {
        captureEvent(EventName.mw_hcaptcha_challenge_opened, { behaviour_type: behaviourType });
      },
      "close-callback": () => {
        captureEvent(EventName.mw_hcaptcha_challenge_closed, { behaviour_type: behaviourType });
        reject(new Error("hcaptcha challenge closed"));
        cleanup();
      },
      "error-callback": (err: unknown) => {
        logger.info("[capcha] error", err);
        captureEvent(EventName.mw_hcaptcha_failed, {
          error: err,
          behaviour_type: behaviourType,
        });
        reject(new Error("hcaptcha failed"));
        cleanup();
      },
    });

    hcaptcha.execute(widget);
    function cleanup() {
      try {
        hcaptcha.remove(widget);
        document.body.removeChild(container);
      } catch (e) {
        logger.error(e instanceof Error ? e : null, "[captcha] error removing widget");
      }
    }
  });
}
